// ===============================================================
// ✅ GenderThemeContext.js — canonical, closed-set active gender theme
// ===============================================================
//
// Consumes AccountSessionStateContext's single, shared users/{uid} read
// (see AccountSessionStateContext.js) instead of performing its own
// second read of the same document - this is the exact same
// gender-resolution logic ThemeBridge/ThemeBridgeProvider always used,
// only the data source moved.
//
// This provider still does two things with that one resolved value:
//   1. (unchanged) writes "male"/"female" to document.documentElement's
//      data-theme attribute, for theme.css's [data-theme="male"/"female"]
//      CSS selectors. This DOM write intentionally keeps its OWN original
//      mapping (signed-out/loading/no-doc -> "male") rather than the
//      shared context's "default" reset value - theme.css has no
//      [data-theme="default"] rule, and this attribute is read by CSS on
//      every page, including pages rendered before an account resolves,
//      so it must never go unstyled. See below for why the React context
//      value is allowed to diverge from this DOM attribute.
//   2. (unchanged) also publishes a genuinely closed-set
//      "male" | "female" | "default" value through this React context -
//      "default" during loading/signed-out/mid-resolution, matching
//      AccountSessionStateContext's own reset semantics.
//
// Why a separate channel instead of reading data-theme back: that DOM
// attribute is NOT exclusively owned by this bridge - src/theme.js's
// applyTheme() (wired only from the Appearance settings page,
// src/settings/ThemeSettings.js's selectMode) can ALSO write "dark" or
// "light" to the exact same attribute. This context is written ONLY here
// and is never touched by theme.js/ThemeSettings.js, so it cannot be
// affected by a dark/light mode change, ever.

import React, { createContext, useContext, useEffect } from "react";
import { useAccountSessionState } from "./AccountSessionStateContext";

export const GENDER_THEME_MALE = "male";
export const GENDER_THEME_FEMALE = "female";
export const GENDER_THEME_DEFAULT = "default";

export const GenderThemeContext = createContext(GENDER_THEME_DEFAULT);

export function useGenderTheme() {
  return useContext(GenderThemeContext);
}

export function ThemeBridgeProvider({ children }) {
  const { genderTheme } = useAccountSessionState();

  useEffect(() => {
    // The DOM attribute keeps its original two-value mapping - "default"
    // (loading/signed-out/reset) resolves to "male" here exactly as the
    // prior implementation always defaulted to MALE in those same cases.
    const domTheme = genderTheme === GENDER_THEME_FEMALE ? GENDER_THEME_FEMALE : GENDER_THEME_MALE;
    document.documentElement.setAttribute("data-theme", domTheme);
  }, [genderTheme]);

  return (
    <GenderThemeContext.Provider value={genderTheme}>{children}</GenderThemeContext.Provider>
  );
}
